import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Card, Row, Col, Button, Modal } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ImageMagnifier from "./ImageMagnifier";
import Header from "./Header";
import Footer from "./Footer";
import firebaseApp from "./fireconnect";
import Slider from "react-slick";
import HeadingNavbar from "./HeadingNavbar";
import ProductCard from "./ProductCard";
import { toast, ToastContainer } from "react-toastify";
import { useDataContext } from "./DataContext";

const ProductDetails = () => {
  const { id } = useParams();
  const { productDetails, setProductDetails } = useDataContext();

  const [product, setProduct] = useState(productDetails[id] || null);
  const [fullName, setFullName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [address, setAddress] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [currentImage, setCurrentImage] = useState(productDetails[id]?.image?.[0] || "");
  const [loading, setLoading] = useState(!productDetails[id]);
  const [isMobile, setIsMobile] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(!!productDetails[id]);

  const preloadImages = useCallback((imageUrls) => {
    const promises = imageUrls.map((url) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = url;
        img.onload = resolve;
        img.onerror = reject;
      });
    });
    return Promise.all(promises);
  }, []);

  const fetchProduct = useCallback(async () => {
    // If data is already cached, use it
    if (productDetails[id]) {
      setProduct(productDetails[id]);
      setCurrentImage(productDetails[id].image[0]);
      setImagesLoaded(true);
      setLoading(false);
      return;
    }

    const db = firebaseApp.firestore();
    try {
      const productDoc = await db.collection("Product").doc(id).get();
      if (productDoc.exists) {
        const productData = productDoc.data();
        await preloadImages(productData.image);
        setProduct(productData);
        setCurrentImage(productData.image[0]);
        setImagesLoaded(true);

        // Update cache
        setProductDetails((prev) => ({ ...prev, [id]: productData }));
      } else {
        console.warn(`Product with ID ${id} does not exist.`);
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
    } finally {
      setLoading(false);
    }
  }, [id, preloadImages, productDetails, setProductDetails]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  useEffect(() => {
    if (product && product.category) {
      const fetchCategoryProducts = async () => {
        let db = firebaseApp.firestore();
        let pdata = [];
        try {
          const querySnapshot = await db
            .collection("Product")
            .where("category", "==", product.category)
            .get();
          querySnapshot.forEach((doc) => {
            pdata.push({ ...doc.data(), id: doc.id });
          });

          const filteredProducts = pdata.filter((Item) => Item.id !== id);
          await Promise.all(
            filteredProducts.map((product) => preloadImages(product.image))
          );
          setCategoryProducts(filteredProducts);
        } catch (error) {
          console.error("Error fetching products in the same category:", error);
        }
      };

      fetchCategoryProducts();
    }
  }, [product, id, preloadImages]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 992);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: product?.pname,
          url: window.location.href,
        })
        .catch((error) => console.log("Error sharing", error));
    } else {
      alert("Sharing is not supported on this device.");
    }
  };

  const handleMouseEnter = (img) => {
    setCurrentImage(img);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(price);
  };

  const generateWhatsAppMessage = () => {
    let message = "Order Details :\n\n";
    message += `Full Name: ${fullName}\n`;
    message += `Mobile Number: ${mobileNumber}\n`;
    message += `Address: ${address}\n\n`;

    message += "My Order :\n\n";
    message += `Product Name: ${product?.pname || "Not available"}\n\n`;
    message += `Category: ${product?.category || "Not available"}\n\n`;
    message += `Price: ₹${product?.discountedPrice || product?.price || "Not available"
      }\n\n`;
    message += `Total Price: ₹${product?.discountedPrice || product?.price || "Not available"
      }`;

    return message;
  };

  const handleWhatsAppOrder = async () => {
    if (!fullName || !mobileNumber || !address) {
      toast.error("Please fill in all the details.");
      return;
    }

    const message = generateWhatsAppMessage();
    const phoneNumber = "+919265199516";
    const encodedMessage = encodeURIComponent(message);
    const whatsappLink = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappLink, "_blank");

    const db = firebaseApp.firestore();

    const order = {
      userName: fullName,
      mobileNumber: mobileNumber,
      address: address,
      pname: product?.pname || "Not available",
      pdetails: product?.pdetails || "No details available",
      category: product?.category || "Not available",
      price: product?.price || "Not available",
      discountedPrice: product?.discountedPrice || null,
      status: "Pending",
      createdAt: new Date(),
      image: product?.image[0] || "",
    };

    try {
      await db.collection("Orders").add(order);
      toast.success("Your order has been submitted successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error("Error submitting order: ", error);
      toast.error("Failed to submit order. Please try again.");
    }

    setShowModal(false);
  };

  const handleBuyNow = () => {
    setShowModal(true);
  };

  const PrevArrow = React.forwardRef((props, ref) => {
    const { className, style, onClick } = props;
    return (
      <div
        ref={ref}
        className={`${className} custom-prev-arrow`}
        style={{
          ...style,
          left: "10px",
          zIndex: 1,
        }}
        onClick={onClick}
      />
    );
  });

  const NextArrow = React.forwardRef((props, ref) => {
    const { className, style, onClick } = props;
    return (
      <div
        ref={ref}
        className={`${className} custom-next-arrow`}
        style={{
          ...style,
          right: "10px",
          zIndex: 1,
        }}
        onClick={onClick}
      />
    );
  });

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 5,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 4,
        },
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: 576,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
    ],
  };

  const settings2 = {
    dots: true,
    infinite: product && product.image && product.image.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    adaptiveHeight: true,
  };

  return (
    <>
      <HeadingNavbar />
      <Header />
      <div className="container mt-4">
        {loading ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-square">
              <div className="square-1 square"></div>
              <div className="square-2 square"></div>
              <div className="square-3 square"></div>
            </div>
          </div>
        ) : (
          <Row className="">
            <Button
              variant="link"
              className="d-flex justify-content-end align-items-start pe-4"
              onClick={handleShare}
            >
              <i className="bi bi-share-fill fs-4 ShareIcon2 d-lg-none d-block"></i>
            </Button>
            <Col
              lg={1}
              md={12}
              sm={12}
              className="thumbnailPart order-lg-1 order-2 d-flex flex-lg-column flex-md-row align-items-center p-0 px-lg-0 px-3 mb-lg-0 mb-4"
            >
              {imagesLoaded &&
                product?.image?.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    loading="eager"
                    alt={`Thumbnail ${index + 1}`}
                    className="img-thumbnail mt-lg-0 mt-3 mb-lg-2 mb-0 me-lg-0 me-2 rounded-1"
                    style={{
                      border:
                        currentImage === img
                          ? "2px solid #4b9fcc"
                          : "1px solid rgb(225, 225, 225)",
                      cursor: "pointer",
                      transition: "border 0.2s ease",
                    }}
                    onClick={() => setCurrentImage(img)}
                    onMouseEnter={() => handleMouseEnter(img)}
                  />
                ))}
            </Col>

            <Col
              lg={5}
              md={12}
              sm={12}
              className="order-lg-2 order-1 px-3 d-flex"
            >
              <Card className="shadow-none w-100 border-0">
                {isMobile ? (
                  <Slider {...settings2} className="h-auto">
                    {product?.image?.map((img, index) => (
                      <div
                        key={index}
                        className="d-flex justify-content-center productmainimage_bg mb-3"
                      >
                        <img
                          src={img}
                          loading="eager"
                          alt={`Product ${index + 1}`}
                          className="product-main-image"
                        />
                      </div>
                    ))}
                  </Slider>
                ) : (
                  <div className="ImageMagnifierIMG p-4 border rounded">
                    <div>
                      {imagesLoaded && (
                        <ImageMagnifier
                          src={currentImage}
                          width="100%"
                          height="100%"
                          magnifierHeight={200}
                          magnifierWidth={200}
                          zoomLevel={2.5}
                        />
                      )}
                    </div>
                  </div>
                )}
              </Card>
              <div className="ps-lg-2">
                <Button
                  variant="link"
                  className="d-flex justify-content-center align-items-start p-0"
                  onClick={handleShare}
                >
                  <i className="bi bi-box-arrow-up fs-4 ShareIcon mb-2 d-lg-block d-none"></i>
                </Button>
              </div>
            </Col>

            <Col
              lg={6}
              md={12}
              sm={12}
              className="order-lg-3 order-3 px-md-2 px-0 mt-lg-0 mt-4"
            >
              <Card className="shadow-none border-0 px-3">
                <Card.Body className="p-0">
                  <Card.Title className="product-card-title text-start mb-3 text-dark fw-bold text-capitalize">
                    {product?.pname}
                  </Card.Title>
                  <Card.Subtitle className="my-2 text-muted text-capitalize">
                    {product?.category}
                  </Card.Subtitle>
                  <h4 className="text-dark my-3">
                    <span className="me-2 fs-3">
                      {formatPrice(product?.discountedPrice || product?.price)}
                    </span>
                    {product?.discountedPrice && (
                      <small className="text-success">
                        <del className="me-2 text-danger fs-5">
                          {formatPrice(product?.price)}
                        </del>
                        (
                        {Math.round(
                          ((product?.price - product?.discountedPrice) /
                            product?.price) *
                          100
                        )}
                        % off)
                      </small>
                    )}
                  </h4>
                  <div
                    className="ql-editor"
                    dangerouslySetInnerHTML={{ __html: product?.pdetails }}
                  />
                  <div className="mt-4 d-flex align-items-center">
                    <button className="productOrderBtn" onClick={handleBuyNow}>
                      <span className="productOrderBtnSpan">BUY NOW</span>
                    </button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        <section>
          <div className="mb-4">
            {!loading && categoryProducts.length > 0 && (
              <>
                <div>
                  <h5 className="mt-5 pb-2 fw-bold">Related Products</h5>
                </div>
                <Slider {...settings}>
                  {categoryProducts.map((item) => (
                    <div key={item.id}>
                      <ProductCard product={item} />
                    </div>
                  ))}
                </Slider>
              </>
            )}
          </div>
        </section>

        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Enter Your Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label text-dark mb-0">Full Name</label>
              <input
                type="text"
                className="form-control"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label text-dark mb-0">Mobile Number</label>
              <input
                type="text"
                className="form-control"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label text-dark mb-0">Address</label>
              <textarea
                className="form-control"
                rows="3"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={handleWhatsAppOrder}>
              Submit Order
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
      <Footer />
      <ToastContainer />
    </>
  );
};

export default ProductDetails;
